import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers, title }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = React.useRef();

  // Reset search fields and results when modal closes
  React.useEffect(() => {
    if (!show) {
      setSearchFields({
        title: '',
        forename: '',
        surname: '',
        email: '',
        phone_number: ''
      });
      setSearchResults([]);
    }
  }, [show]);

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export function SimpleConveyancingForm() {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const authCtx = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId');
  // Use customerIdFromUrl if available, otherwise use customerId from route params
  const customerIdToUse = customerIdFromUrl || customerId;

  const [formData, setFormData] = useState({
    overrideDate: '',
    groupCode: '',
    acquisition: '',
    typeOfService: ''
  });

  const [groupCodes, setGroupCodes] = useState([]);
  const [customerData, setCustomerData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showPurchaserModal, setShowPurchaserModal] = useState(false);

  // Acquisition options
  const acquisitionOptions = [
    { value: 'Inbound', label: 'Inbound' },
    { value: 'Cold-Acquisition', label: 'Cold Acquisition' }
  ];

  // Type of Service options
  const typeOfServiceOptions = [
    { value: 'Purchase', label: 'Purchase' },
    { value: 'Sale', label: 'Sale' },
    { value: 'Purchase_Sale', label: 'Sale & Purchase' },
    { value: 'Transfer_of_Equity', label: 'Transfer of Equity' },
    { value: 'Remortgage', label: 'Remortgage' },
    { value: 'Mortgage_Redemption', label: 'Mortgage Redemption' },
    { value: 'First_Registration', label: 'First Registration' },
   { value: 'Review_of_Legal_Pack', label: 'Review of Legal Pack' }
  ];

  // Fetch Group Codes
  const fetchGroupCodes = async () => {
    try {
      const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res?.data)) {
        setGroupCodes(res.data);
      }
    } catch (error) {
      console.error('Error fetching group codes:', error);
    }
  };

  // Fetch Customer Details
  const fetchCustomerDetails = async (id) => {
    try {
      // API call disabled as per requirement
      // const response = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
      // if (response?.status === 200 && response?.data) {
      //   setCustomerData(response.data);
      // }
    } catch (err) {
      // Silently handle error - don't log to console
      // console.error('Error fetching customer details', err);
    }
  };

  useEffect(() => {
    if (authCtx.token) {
      fetchGroupCodes();
      // Disabled: if (customerIdToUse) {
      //   fetchCustomerDetails(customerIdToUse);
      // }
    }
  }, [authCtx.token, customerIdToUse]);

  const handleAddVendor = (customer) => {
    if (!vendors.some(v => v._id === customer._id)) {
      setVendors([...vendors, customer]);
    }
  };

  const handleRemoveVendor = (customerId) => {
    setVendors(vendors.filter(v => v._id !== customerId));
  };

  const handleAddPurchaser = (customer) => {
    if (!purchasers.some(p => p._id === customer._id)) {
      setPurchasers([...purchasers, customer]);
    }
  };

  const handleRemovePurchaser = (customerId) => {
    setPurchasers(purchasers.filter(p => p._id !== customerId));
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  const handleContinue = async () => {
    // Validate form data
    if (!formData.groupCode || !formData.acquisition || !formData.typeOfService) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    try {
      const created_id = localStorage.getItem('id');
      
      // First, fetch the Conveyancing product ID
      console.log('🔍 Fetching Conveyancing product...');
      let productId = null;
      
      try {
        const productsRes = await postRequest('private/getproductList', {}, authCtx.token);
        if (productsRes?.data) {
          const conveyancingProduct = productsRes.data.find(product => 
            product.title?.toLowerCase() === 'conveyancing' || 
            product.name?.toLowerCase() === 'conveyancing'
          );
          
          if (conveyancingProduct) {
            productId = conveyancingProduct._id;
            console.log('✅ Found Conveyancing product:', conveyancingProduct.title, 'ID:', productId);
          } else {
            console.log('❌ No Conveyancing product found');
            Swal.fire('Error', 'Conveyancing product not found. Please contact admin.', 'error');
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      }
      
      const payload = {
        override_date: formData.overrideDate,
        group_code: formData.groupCode,
        acquisition: formData.acquisition,
        service_type: formData.typeOfService,  // Changed from type_of_service to service_type
        customers: customerIdToUse ? [customerIdToUse] : [],
        created_id: created_id,
        product_id: productId,  // Include the product ID
        product_title: 'Conveyancing'
      };

      // Add vendors for Sale and Purchase_Sale
      if (formData.typeOfService === 'Sale' || formData.typeOfService === 'Purchase_Sale') {
        payload.vendors = vendors.map(v => v._id);
      }

      // Add purchasers for Purchase and Purchase_Sale
      if (formData.typeOfService === 'Purchase' || formData.typeOfService === 'Purchase_Sale') {
        payload.purchasers = purchasers.map(p => p._id);
      }

      // Navigate directly to detail page with basic form data
      // The detail page will create the lead when saving
      const navigationState = {
        basicFormData: payload
      };

      // Navigate to the appropriate screen based on type of service
      if (formData.typeOfService === 'Purchase') {
        navigate(`/conveyancing/purchase/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else if (formData.typeOfService === 'Sale') {
        navigate(`/conveyancing/sale/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else if (formData.typeOfService === 'Purchase_Sale') {
        navigate(`/conveyancing/sale-purchase/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else if (formData.typeOfService === 'Transfer_of_Equity') {
        navigate(`/conveyancing/transfer-of-equity/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else if (formData.typeOfService === 'Remortgage') {
        navigate(`/conveyancing/remortgage/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else if (formData.typeOfService === 'Mortgage_Redemption') {
        navigate(`/conveyancing/mortgage-redemption/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else if (formData.typeOfService === 'First_Registration') {
        navigate(`/conveyancing/first-registration/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else if (formData.typeOfService === 'Review_of_Legal_Pack') {
        navigate(`/conveyancing/review-of-legal-pack/new${customerIdToUse ? `?customerId=${customerIdToUse}` : ''}`, { state: navigationState });
      } else {
        navigate('/leads'); // Default navigation
      }
    } catch (error) {
      console.error('Error creating conveyancing:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Something went wrong. Please try again.'
      });
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Customers', path: '/customers' },
          { label: 'Create Conveyancing', path: '' },
        ]}
      />
      <br />
      
      <Box sx={{ width: '100%', minHeight: '100vh', p: 3 }}>
        <div className='page-header pb-3'>
          <h1 className='mt-0 mb-0'>Create Conveyancing
            <small className='color-grey'> {customerData ? `${customerData.forename || ''} ${customerData.surname || ''}` : ''}</small>
          </h1>
        </div>
        
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
        
        <Box component="form" sx={{ '& > :not(style)': { mb: 3 } }}>
          {/* Override Date */}
          <TextField
            fullWidth
            label="Override Date"
            type="date"
            value={formData.overrideDate}
            onChange={handleChange('overrideDate')}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />

          {/* Group Code */}
          <FormControl fullWidth variant="outlined">
            <InputLabel>Group Code</InputLabel>
            <Select
              value={formData.groupCode}
              onChange={handleChange('groupCode')}
              label="Group Code"
            >
              <MenuItem value="">Select Group Code</MenuItem>
              {groupCodes.map((groupCode) => (
                <MenuItem key={groupCode._id} value={groupCode._id}>
                  {groupCode.name} ({groupCode.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Acquisition */}
          <FormControl fullWidth variant="outlined">
            <InputLabel>Acquisition</InputLabel>
            <Select
              value={formData.acquisition}
              onChange={handleChange('acquisition')}
              label="Acquisition"
            >
              <MenuItem value="">Select Acquisition</MenuItem>
              {acquisitionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Type of Service */}
          <FormControl fullWidth variant="outlined">
            <InputLabel>Type of Service</InputLabel>
            <Select
              value={formData.typeOfService}
              onChange={handleChange('typeOfService')}
              label="Type of Service"
            >
              <MenuItem value="">Select Type of Service</MenuItem>
              {typeOfServiceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                color: '#d32f2f',
                borderColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#ffebee',
                  borderColor: '#d32f2f'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleContinue}
              sx={{
                backgroundColor: '#7c3aed',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#6d28d9'
                }
              }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>

    {/* Vendor Search Modal */}
    <CustomerSearchModal
      show={showVendorModal}
      onHide={() => setShowVendorModal(false)}
      authToken={authCtx.token}
      onCustomerAdd={handleAddVendor}
      selectedCustomers={vendors}
      title="Add Vendor"
    />

    {/* Purchaser Search Modal */}
    <CustomerSearchModal
      show={showPurchaserModal}
      onHide={() => setShowPurchaserModal(false)}
      authToken={authCtx.token}
      onCustomerAdd={handleAddPurchaser}
      selectedCustomers={purchasers}
      title="Add Purchaser"
    />
    </>
  );
}

export default SimpleConveyancingForm;